/* eslint-disable no-underscore-dangle */
import { SearchOutlined } from "@mui/icons-material";
import {
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Tooltip,
  IconButton,
  TablePagination,
} from "@mui/material";
import { ChangeEvent, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  EnhancedTableHead,
  IHeadCell,
} from "../../components/EnhancedTableHead";
import { IProperty } from "../../typos/properties";
import { stableSort, getComparator, TOrder } from "../../utils";
import { PropertyType, TransactionType } from "../../mocks";
import PropertyViewDialog from "./view-dialog";
import { PropertyStatusTag } from "../../components/PropertyStatusTag";
import { NumericFormatCustom } from "../../components/NumericFormatCustom";

interface IAdsTableProps {
  onChangeRowsSelected(n: readonly { code: number; id: string }[]): void;
  updateRefreshCount(): void;
  rows: IProperty[];
  rowsSelected: readonly { code: number; id: string }[];
}

const headCells: readonly IHeadCell[] = [
  {
    id: "code",
    numeric: false,
    disablePadding: false,
    label: "Cód.",
  },
  {
    id: "status",
    numeric: false,
    disablePadding: false,
    label: "Status",
  },
  {
    id: "transactionType",
    numeric: false,
    disablePadding: false,
    label: "Transacción",
  },
  {
    id: "type",
    numeric: false,
    disablePadding: false,
    label: "Tipo",
  },
  {
    id: "address.neighbourhood" as keyof IProperty,
    numeric: false,
    disablePadding: false,
    label: "Dirección",
  },
  {
    id: "created_at",
    numeric: false,
    disablePadding: false,
    label: "Creado en",
  },
  {
    id: "updated_at",
    numeric: false,
    disablePadding: false,
    label: "Actualizado en",
  },
  {
    id: "price",
    numeric: false,
    disablePadding: false,
    label: "Precio",
  },
  {
    id: "accountId",
    numeric: false,
    disablePadding: false,
    label: "Account ID",
  },
  {
    id: "actions" as any,
    numeric: false,
    disablePadding: false,
    label: "",
  },
];

export default function AdsTable({
  rows,
  rowsSelected,
  onChangeRowsSelected,
  updateRefreshCount,
}: IAdsTableProps) {
  const [order, setOrder] = useState<TOrder>("desc");
  const [orderBy, setOrderBy] = useState<keyof IProperty>("id");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rowDetail, setRowDetail] = useState<null | IProperty>(null);

  const handleRequestSort = (e: any, property: keyof IProperty) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const newSelected = rows.map((n: any) => ({
        code: n.code,
        id: n.id,
      }));
      onChangeRowsSelected(newSelected);
      return;
    }

    onChangeRowsSelected([]);
  };

  const handleClick = (e: any, row: IProperty) => {
    const selectedIndex = rowsSelected.findIndex(
      (item) => item.code === row.code,
    );
    let newSelected: readonly { code: number; id: string }[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(rowsSelected, {
        code: row.code,
        id: row.id,
      });
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(rowsSelected.slice(1));
    } else if (selectedIndex === rowsSelected.length - 1) {
      newSelected = newSelected.concat(rowsSelected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        rowsSelected.slice(0, selectedIndex),
        rowsSelected.slice(selectedIndex + 1),
      );
    }

    onChangeRowsSelected(newSelected);
  };

  const handleChangePage = (e: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const isSelected = (code: number) =>
    !!rowsSelected.find((item) => item.code === code);

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows: IProperty[] = useMemo(() => {
    const cpRows: any = rows;

    const resp: any = stableSort(cpRows, getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );

    return resp;
  }, [order, orderBy, page, rows, rowsPerPage]);

  const tableCellStyle = useMemo(() => "!text-[13px]", []);

  return (
    <>
      <TableContainer>
        <Table
          sx={{ minWidth: 750 }}
          aria-labelledby="tableTitle"
          size="medium"
        >
          <EnhancedTableHead
            numSelected={rowsSelected.length}
            order={order}
            orderBy={orderBy.toString()}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={rows.length}
            cells={headCells}
          />

          <TableBody>
            {visibleRows.length > 0 ? (
              visibleRows.map((row: IProperty, index: number) => {
                const isItemSelected = isSelected(row.code);
                const labelId = `enhanced-table-checkbox-${index}`;
                const address = [
                  row.address?.street,
                  row.address?.neighbourhood,
                  row.address?.city,
                  row.address.state,
                ]
                  .filter(Boolean)
                  .join(", ");

                return (
                  <TableRow hover key={row.id} selected={isItemSelected}>
                    <TableCell className={tableCellStyle} padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        onClick={(e) => handleClick(e, row)}
                        inputProps={{
                          "aria-labelledby": labelId,
                        }}
                      />
                    </TableCell>

                    <TableCell className={tableCellStyle}>{row.code}</TableCell>

                    <TableCell className={tableCellStyle}>
                      <PropertyStatusTag status={row.status} />
                    </TableCell>

                    <TableCell className={tableCellStyle}>
                      {TransactionType[row.transactionType]}
                    </TableCell>

                    <TableCell className={tableCellStyle}>
                      {PropertyType[row.type]}
                    </TableCell>

                    <TableCell className={tableCellStyle} align="left">
                      {address}
                    </TableCell>

                    <TableCell className={tableCellStyle}>
                      {row.createdAt
                        ? format(row.createdAt, "dd/MM/yyyy")
                        : "-"}
                    </TableCell>

                    <TableCell className={tableCellStyle}>
                      {format(row.updatedAt, "dd/MM/yyyy")}
                    </TableCell>

                    <TableCell className={tableCellStyle}>
                      <NumericFormatCustom
                        displayType="text"
                        name="price"
                        value={row.price}
                      />
                    </TableCell>

                    <TableCell className={tableCellStyle}>
                      {row.accountId}
                    </TableCell>

                    <TableCell
                      align="right"
                      className={`whitespace-nowrap ${tableCellStyle}`}
                    >
                      <Tooltip title="Visualizar">
                        <IconButton onClick={() => setRowDetail(row)}>
                          <SearchOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow
                style={{
                  height: 53 * emptyRows,
                }}
              >
                <TableCell colSpan={11} align="center">
                  Ninguna publicación registrada
                </TableCell>
              </TableRow>
            )}

            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: 53 * emptyRows,
                }}
              >
                <TableCell colSpan={11} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        getItemAriaLabel={(type) => `Ir a la página ${type}`}
        labelRowsPerPage="Líneas por página:"
        labelDisplayedRows={({ from, to, count }) => {
          return `${from}-${to} de ${count !== -1 ? count : `más que ${to}`}`;
        }}
      />

      {!!rowDetail && (
        <PropertyViewDialog
          property={rowDetail}
          onClose={() => setRowDetail(null)}
          onSuccess={() => {
            setRowDetail(null);
            updateRefreshCount();
          }}
        />
      )}
    </>
  );
}
